import {useEffect, useReducer, useState} from "react";
import {detectInstallPlatform, installOffer} from "./installOffer";
import {installPromptStore} from "./installPromptStore";
import {createInstallDismissal} from "./installDismissal";

const INSTALLED_DISPLAY_MODES = ['standalone', 'fullscreen', 'minimal-ui'];

const defaultDismissal = createInstallDismissal();

const browserEnv = () => ({
    matchMedia: (query) => window.matchMedia(query),
    navigator: window.navigator,
    now: () => Date.now(),
});

/**
 * Whether and how to offer installing DAMS as an app (DE-1707, ADR-0005).
 *
 * @param {Object} [deps]  Injected for tests; the app uses the defaults.
 * @returns {{
 *   platform: 'prompt'|'ios'|'none',
 *   showBanner: boolean,
 *   showMenuItem: boolean,
 *   install: function(): Promise<void>,
 *   dismiss: function(): void,
 *   hideBannerForSession: function(): void,
 *   iosDialogOpen: boolean,
 *   closeIosDialog: function(): void
 * }}
 */
export const useInstallApp = ({store = installPromptStore, dismissal = defaultDismissal, env = browserEnv()} = {}) => {
    const [, storeChanged] = useReducer((count) => count + 1, 0);
    const [dismissedAt, setDismissedAt] = useState(() => dismissal.read());
    const [bannerHidden, setBannerHidden] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [iosDialogOpen, setIosDialogOpen] = useState(false);

    useEffect(() => store.subscribe(storeChanged), [store]);

    const platform = accepted ? 'none' : detectInstallPlatform(readDevice(store, env));
    const offer = installOffer({
        platform,
        isCoarsePointer: env.matchMedia('(pointer: coarse)').matches,
        dismissedAt,
        now: env.now(),
    });

    const install = async () => {
        if (platform === 'ios') {
            // The banner stays until the dialog closes, so focus can return to it.
            setIosDialogOpen(true);
            return;
        }
        const event = store.takePromptEvent();
        if (event) {
            setBannerHidden(true);
            setAccepted(await promptAccepted(event));
        }
    };

    const dismiss = () => {
        const now = env.now();
        dismissal.write(now);
        setDismissedAt(now);
    };

    return {
        platform,
        showBanner: offer.showBanner && !bannerHidden,
        showMenuItem: offer.showMenuItem,
        install,
        dismiss,
        hideBannerForSession: () => setBannerHidden(true),
        iosDialogOpen,
        closeIosDialog: () => {
            setIosDialogOpen(false);
            setBannerHidden(true);
        },
    };
};

const readDevice = (store, {matchMedia, navigator}) => ({
    hasPromptEvent: store.promptEvent() !== null,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    isStandalone: store.isInstalled() || navigator.standalone === true
        || INSTALLED_DISPLAY_MODES.some((mode) => matchMedia(`(display-mode: ${mode})`).matches),
});

const promptAccepted = async (event) => {
    await event.prompt();
    const {outcome} = await event.userChoice;
    return outcome === 'accepted';
};
